import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { filenameFromContentDisposition } from '@/lib/download';
import { queryKeys } from '@/lib/query-client';
import type {
  ProjectClient,
  ClientContact,
  Department,
  DepartmentRollupRow,
  Project,
  ProjectPhase,
  ProjectTask,
  ProjectAssignment,
  ProjectNote,
  ProjectDocument,
  Bid,
  Invoice,
  InvoicePayment,
  TimeEntry,
  ProjectSummary,
  PaginatedResponse,
  CreateClientPayload,
  UpdateClientPayload,
  CreateClientContactPayload,
  CreateProjectPayload,
  UpdateProjectPayload,
  CreateProjectPhasePayload,
  UpdateProjectPhasePayload,
  CreateProjectTaskPayload,
  UpdateProjectTaskPayload,
  CreateProjectAssignmentPayload,
  CreateProjectNotePayload,
  CreateBidPayload,
  UpdateBidPayload,
  BidGearAvailabilityRow,
  CreateInvoicePayload,
  UpdateInvoicePayload,
  CreateInvoicePaymentPayload,
  CreateTimeEntryPayload,
  UpdateTimeEntryPayload,
  BulkTimeEntryPayload,
  ProjectStatus,
  BidStatus,
  InvoiceStatus,
  ClientPortalAccess,
  CreateClientPortalAccessPayload,
  UpdateClientPortalAccessPayload,
  RecurringInvoiceConfig,
  CreateRecurringInvoiceConfigPayload,
  UpdateRecurringInvoiceConfigPayload,
  ChangeOrder,
  ChangeOrderLineItem,
  CreateChangeOrderPayload,
  UpdateChangeOrderPayload,
  CreateChangeOrderLineItemPayload,
  UpdateChangeOrderLineItemPayload,
  PortalProject,
  PortalBid,
  PortalInvoice,
  PortalChangeOrder,
  PortalSessionInfo,
  ProjectActivityPage,
  PortalSafetyResponse,
  CostCode,
  ProgressSummary,
} from '@/lib/types';

const PROJECTS_API = '/api/v1/projects';

// ─── Filter Types ───────────────────────────────────────────────────────────

export interface ClientFilters {
  page?: number;
  page_size?: number;
  q?: string;
  is_active?: boolean;
  ordering?: string;
}

export interface ProjectFilters {
  page?: number;
  page_size?: number;
  search?: string;
  status?: ProjectStatus | '';
  client?: number;
  department?: number;
  priority?: string;
  ordering?: string;
}

export interface DepartmentFilters {
  page?: number;
  page_size?: number;
  is_active?: boolean;
}

export interface DepartmentRollupFilters {
  date_from?: string;
  date_to?: string;
  export?: 'csv';
}

export interface ProjectTaskFilters {
  page?: number;
  page_size?: number;
  project?: number;
  status?: string;
  assigned_to?: number;
  ordering?: string;
}

export interface BidFilters {
  page?: number;
  page_size?: number;
  q?: string;
  status?: BidStatus | '';
  client?: number;
  ordering?: string;
}

export interface InvoiceFilters {
  page?: number;
  page_size?: number;
  q?: string;
  status?: InvoiceStatus | '';
  project?: number;
  client?: number;
  ordering?: string;
}

export interface TimeEntryFilters {
  page?: number;
  page_size?: number;
  project?: number;
  tech?: number;
  date_from?: string;
  date_to?: string;
  approved?: boolean;
  billable?: boolean;
  ordering?: string;
}

function cleanFilters<T extends object>(filters: T): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== '' && value !== undefined)
  );
}

// ─── Department Hooks ───────────────────────────────────────────────────────

export function useDepartments(filters: DepartmentFilters = {}, enabled = true) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.departments.list(clean),
    queryFn: async (): Promise<PaginatedResponse<Department>> => {
      const response = await apiClient.get<PaginatedResponse<Department>>(
        `${PROJECTS_API}/departments/`,
        { params: clean }
      );
      return response.data;
    },
    enabled,
  });
}

export function useCreateDepartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { name: string; code?: string }): Promise<Department> => {
      const response = await apiClient.post<Department>(`${PROJECTS_API}/departments/`, data);
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.departments.all });
    },
  });
}

export function useUpdateDepartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: { name?: string; code?: string; is_active?: boolean };
    }): Promise<Department> => {
      const response = await apiClient.patch<Department>(
        `${PROJECTS_API}/departments/${id}/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.departments.all });
      qc.invalidateQueries({ queryKey: queryKeys.departments.detail(variables.id) });
    },
  });
}

export function useDepartmentRollup(filters: DepartmentRollupFilters = {}) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.departments.rollup(clean),
    queryFn: async (): Promise<DepartmentRollupRow[]> => {
      const response = await apiClient.get<DepartmentRollupRow[]>(
        `${PROJECTS_API}/department-rollup/`,
        { params: clean }
      );
      return response.data;
    },
  });
}

// ─── Client Hooks ───────────────────────────────────────────────────────────

export function useClients(filters: ClientFilters = {}, enabled = true) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.projectClients.list(clean),
    queryFn: async (): Promise<PaginatedResponse<ProjectClient>> => {
      const response = await apiClient.get<PaginatedResponse<ProjectClient>>(
        `${PROJECTS_API}/clients/`,
        { params: clean }
      );
      return response.data;
    },
    enabled,
  });
}

export function useClient(id: number, enabled = true) {
  return useQuery({
    queryKey: queryKeys.projectClients.detail(id),
    queryFn: async (): Promise<ProjectClient> => {
      const response = await apiClient.get<ProjectClient>(`${PROJECTS_API}/clients/${id}/`);
      return response.data;
    },
    enabled: enabled && id > 0,
  });
}

export function useCreateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateClientPayload): Promise<ProjectClient> => {
      const response = await apiClient.post<ProjectClient>(`${PROJECTS_API}/clients/`, data);
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.projectClients.all });
    },
  });
}

export function useUpdateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateClientPayload }): Promise<ProjectClient> => {
      const response = await apiClient.patch<ProjectClient>(`${PROJECTS_API}/clients/${id}/`, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.projectClients.all });
      qc.invalidateQueries({ queryKey: queryKeys.projectClients.detail(variables.id) });
    },
  });
}

export function useDeleteClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.delete(`${PROJECTS_API}/clients/${id}/`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.projectClients.all });
    },
  });
}

// ─── Client Contact Hooks ───────────────────────────────────────────────────

export function useClientContacts(clientId: number) {
  return useQuery({
    queryKey: [...queryKeys.projectClients.detail(clientId), 'contacts'] as const,
    queryFn: async (): Promise<ClientContact[]> => {
      const response = await apiClient.get<ClientContact[]>(
        `${PROJECTS_API}/clients/${clientId}/contacts/`
      );
      return response.data;
    },
    enabled: clientId > 0,
  });
}

export function useCreateClientContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateClientContactPayload): Promise<ClientContact> => {
      const response = await apiClient.post<ClientContact>(
        `${PROJECTS_API}/clients/${data.client}/contacts/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.projectClients.detail(variables.client) });
    },
  });
}

// ─── Project Hooks ──────────────────────────────────────────────────────────

export function useProjects(filters: ProjectFilters = {}, enabled = true) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.projects.list(clean),
    queryFn: async (): Promise<PaginatedResponse<Project>> => {
      const response = await apiClient.get<PaginatedResponse<Project>>(
        `${PROJECTS_API}/projects/`,
        { params: clean }
      );
      return response.data;
    },
    enabled,
  });
}

async function fetchProject(id: number): Promise<Project> {
  const response = await apiClient.get<Project>(`${PROJECTS_API}/projects/${id}/`);
  return response.data;
}

export function useProject(id: number) {
  return useQuery({
    queryKey: queryKeys.projects.detail(id),
    queryFn: () => fetchProject(id),
    enabled: id > 0,
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateProjectPayload): Promise<Project> => {
      const response = await apiClient.post<Project>(`${PROJECTS_API}/projects/`, data);
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.projects.all });
      qc.invalidateQueries({ queryKey: queryKeys.onboarding.state() });
    },
  });
}

export function useUpdateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateProjectPayload }): Promise<Project> => {
      const response = await apiClient.patch<Project>(`${PROJECTS_API}/projects/${id}/`, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.projects.all });
      qc.invalidateQueries({ queryKey: queryKeys.projects.detail(variables.id) });
    },
  });
}

export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.delete(`${PROJECTS_API}/projects/${id}/`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.projects.all });
    },
  });
}

export function useProjectSummary(id: number, enabled = true) {
  return useQuery({
    queryKey: queryKeys.projects.summary(id),
    queryFn: async (): Promise<ProjectSummary> => {
      const response = await apiClient.get<ProjectSummary>(
        `${PROJECTS_API}/projects/${id}/summary/`
      );
      return response.data;
    },
    enabled: id > 0 && enabled,
  });
}

export interface ProjectQRCodeResponse {
  qr_url: string;
  qr_data_url: string;
  project_name: string;
  project_number: string;
  location: string;
}

export function useProjectQRCode(projectId: number, enabled = false) {
  return useQuery({
    queryKey: [...queryKeys.projects.detail(projectId), 'qr-code'],
    queryFn: async (): Promise<ProjectQRCodeResponse> => {
      const response = await apiClient.get<ProjectQRCodeResponse>(
        `${PROJECTS_API}/projects/${projectId}/qr-code/`,
        { params: { format: 'json' } }
      );
      return response.data;
    },
    enabled: projectId > 0 && enabled,
  });
}

// ─── Audit Bundle (S14) ─────────────────────────────────────────────────────

export interface AuditBundleJob {
  id: string;
  organization: number;
  project: number | null;
  project_name: string | null;
  date_from: string | null;
  date_to: string | null;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  error_message: string;
  file_size_bytes: number | null;
  manifest: Record<string, unknown>;
  requested_by: number | null;
  requested_by_username: string | null;
  download_url: string | null;
  created_at: string;
  completed_at: string | null;
}

export interface ExportAuditBundlePayload {
  date_from?: string;
  date_to?: string;
}

export function useExportAuditBundle(projectId: number) {
  return useMutation({
    mutationFn: async (payload: ExportAuditBundlePayload = {}): Promise<AuditBundleJob> => {
      const response = await apiClient.post<AuditBundleJob>(
        `${PROJECTS_API}/projects/${projectId}/audit-bundle/`,
        payload
      );
      return response.data;
    },
  });
}

export function useUpdateProjectStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: number; status: ProjectStatus }): Promise<Project> => {
      const response = await apiClient.post<Project>(
        `${PROJECTS_API}/projects/${id}/transition/`,
        { status }
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.projects.all });
      qc.invalidateQueries({ queryKey: queryKeys.projects.detail(variables.id) });
    },
  });
}

// ─── Progress Billing / Schedule of Values ──────────────────────────────────

export function useProgressSummary(projectId: number) {
  return useQuery({
    queryKey: [...queryKeys.projects.detail(projectId), 'progress-summary'] as const,
    queryFn: async (): Promise<ProgressSummary> => {
      const response = await apiClient.get<ProgressSummary>(
        `${PROJECTS_API}/projects/${projectId}/progress-summary/`
      );
      return response.data;
    },
    enabled: projectId > 0,
  });
}

export function useGenerateProgressInvoice(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<Invoice> => {
      const response = await apiClient.post<Invoice>(
        `${PROJECTS_API}/projects/${projectId}/generate-progress-invoice/`
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.projects.detail(projectId) });
      qc.invalidateQueries({ queryKey: queryKeys.invoices.all });
    },
  });
}

// ─── Project Phase Hooks ────────────────────────────────────────────────────

export function useProjectPhases(projectId: number) {
  return useQuery({
    queryKey: queryKeys.projectPhases.list({ project: projectId }),
    queryFn: async (): Promise<ProjectPhase[]> => {
      const response = await apiClient.get<PaginatedResponse<ProjectPhase>>(
        `${PROJECTS_API}/phases/`,
        { params: { project: projectId, page_size: 1000 } }
      );
      return response.data.results ?? [];
    },
    enabled: projectId > 0,
  });
}

export function useCreateProjectPhase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateProjectPhasePayload): Promise<ProjectPhase> => {
      const response = await apiClient.post<ProjectPhase>(
        `${PROJECTS_API}/phases/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, data) => {
      qc.invalidateQueries({ queryKey: queryKeys.projectPhases.lists() });
      qc.invalidateQueries({ queryKey: queryKeys.projects.detail(data.project) });
    },
  });
}

export function useUpdateProjectPhase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateProjectPhasePayload }): Promise<ProjectPhase> => {
      const response = await apiClient.patch<ProjectPhase>(
        `${PROJECTS_API}/phases/${id}/`,
        data
      );
      return response.data;
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: queryKeys.projectPhases.lists() });
      qc.invalidateQueries({ queryKey: queryKeys.projects.detail(result.project) });
    },
  });
}

export function useDeleteProjectPhase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.delete(`${PROJECTS_API}/phases/${id}/`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.projectPhases.lists() });
      qc.invalidateQueries({ queryKey: queryKeys.projects.all });
    },
  });
}

// ─── Project Task Hooks ─────────────────────────────────────────────────────

export function useProjectTasks(filters: ProjectTaskFilters = {}) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.projectTasks.list(clean),
    queryFn: async (): Promise<PaginatedResponse<ProjectTask>> => {
      const response = await apiClient.get<PaginatedResponse<ProjectTask>>(
        `${PROJECTS_API}/tasks/`,
        { params: clean }
      );
      return response.data;
    },
  });
}

export function useCreateProjectTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateProjectTaskPayload): Promise<ProjectTask> => {
      const response = await apiClient.post<ProjectTask>(`${PROJECTS_API}/tasks/`, data);
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.projectTasks.all });
      qc.invalidateQueries({ queryKey: queryKeys.projects.all });
    },
  });
}

export function useUpdateProjectTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateProjectTaskPayload }): Promise<ProjectTask> => {
      const response = await apiClient.patch<ProjectTask>(`${PROJECTS_API}/tasks/${id}/`, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.projectTasks.all });
      qc.invalidateQueries({ queryKey: queryKeys.projectTasks.detail(variables.id) });
    },
  });
}

export function useDeleteProjectTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.delete(`${PROJECTS_API}/tasks/${id}/`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.projectTasks.all });
    },
  });
}

// ─── Project Assignment Hooks ───────────────────────────────────────────────

export function useProjectAssignments(projectId: number) {
  return useQuery({
    queryKey: [...queryKeys.projects.detail(projectId), 'assignments'] as const,
    queryFn: async (): Promise<ProjectAssignment[]> => {
      const response = await apiClient.get<ProjectAssignment[]>(
        `${PROJECTS_API}/projects/${projectId}/assignments/`
      );
      return response.data;
    },
    enabled: projectId > 0,
  });
}

export function useCreateProjectAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateProjectAssignmentPayload): Promise<ProjectAssignment> => {
      const response = await apiClient.post<ProjectAssignment>(
        `${PROJECTS_API}/assignments/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.projectAssignments.all });
      qc.invalidateQueries({ queryKey: queryKeys.projects.all });
    },
  });
}

export function useDeleteProjectAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.delete(`${PROJECTS_API}/assignments/${id}/`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.projectAssignments.all });
      qc.invalidateQueries({ queryKey: queryKeys.projects.all });
    },
  });
}

// ─── Project Note Hooks ─────────────────────────────────────────────────────

// The backend has no GET /projects/{id}/notes/ route (only POST add-note);
// notes come nested on the project detail as `project_notes`.
export function useProjectNotes(projectId: number) {
  return useQuery({
    queryKey: queryKeys.projects.detail(projectId),
    queryFn: () => fetchProject(projectId),
    select: (project: Project): ProjectNote[] => project.project_notes ?? [],
    enabled: projectId > 0,
  });
}

export function useCreateProjectNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectId,
      data,
    }: {
      projectId: number;
      data: CreateProjectNotePayload;
    }): Promise<ProjectNote> => {
      const response = await apiClient.post<ProjectNote>(
        `${PROJECTS_API}/projects/${projectId}/add-note/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.projects.detail(variables.projectId) });
      qc.invalidateQueries({ queryKey: queryKeys.projectNotes.all });
    },
  });
}

// ─── Project Document Hooks ─────────────────────────────────────────────────

// The backend has no GET /projects/{id}/documents/ route (only POST add-document);
// documents come nested on the project detail as `documents`.
export function useProjectDocuments(projectId: number) {
  return useQuery({
    queryKey: queryKeys.projects.detail(projectId),
    queryFn: () => fetchProject(projectId),
    select: (project: Project): ProjectDocument[] => project.documents ?? [],
    enabled: projectId > 0,
  });
}

export function useUploadProjectDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectId,
      data,
    }: {
      projectId: number;
      data: FormData;
    }): Promise<ProjectDocument> => {
      const response = await apiClient.post<ProjectDocument>(
        `${PROJECTS_API}/projects/${projectId}/add-document/`,
        data,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.projects.detail(variables.projectId) });
      qc.invalidateQueries({ queryKey: queryKeys.projectDocuments.all });
    },
  });
}

// ─── Bid Hooks ──────────────────────────────────────────────────────────────

export function useBids(filters: BidFilters = {}) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.bids.list(clean),
    queryFn: async (): Promise<PaginatedResponse<Bid>> => {
      const response = await apiClient.get<PaginatedResponse<Bid>>(
        `${PROJECTS_API}/bids/`,
        { params: clean }
      );
      return response.data;
    },
  });
}

export function useBid(id: number) {
  return useQuery({
    queryKey: queryKeys.bids.detail(id),
    queryFn: async (): Promise<Bid> => {
      const response = await apiClient.get<Bid>(`${PROJECTS_API}/bids/${id}/`);
      return response.data;
    },
    enabled: id > 0,
  });
}

export function useCreateBid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateBidPayload): Promise<Bid> => {
      const response = await apiClient.post<Bid>(`${PROJECTS_API}/bids/`, data);
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.bids.all });
    },
  });
}

export function useUpdateBid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateBidPayload }): Promise<Bid> => {
      const response = await apiClient.patch<Bid>(`${PROJECTS_API}/bids/${id}/`, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.bids.all });
      qc.invalidateQueries({ queryKey: queryKeys.bids.detail(variables.id) });
    },
  });
}

export function useSendBid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<Bid> => {
      const response = await apiClient.post<Bid>(`${PROJECTS_API}/bids/${id}/send/`);
      return response.data;
    },
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: queryKeys.bids.all });
      qc.invalidateQueries({ queryKey: queryKeys.bids.detail(id) });
    },
  });
}

export function useAcceptBid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<Bid> => {
      const response = await apiClient.post<Bid>(`${PROJECTS_API}/bids/${id}/accept/`);
      return response.data;
    },
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: queryKeys.bids.all });
      qc.invalidateQueries({ queryKey: queryKeys.bids.detail(id) });
      qc.invalidateQueries({ queryKey: queryKeys.projects.all });
      qc.invalidateQueries({ queryKey: queryKeys.onboarding.state() });
    },
  });
}

export function useDuplicateBid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<Bid> => {
      const response = await apiClient.post<Bid>(`${PROJECTS_API}/bids/${id}/duplicate/`);
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.bids.all });
    },
  });
}

export function useDownloadBidPdf() {
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      const response = await apiClient.get(`${PROJECTS_API}/bids/${id}/pdf/`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        filenameFromContentDisposition(
          response.headers['content-disposition'],
          `Bid-${id}.pdf`,
        ),
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
  });
}

export function useBidAvailability(id: number) {
  return useQuery({
    queryKey: [...queryKeys.bids.detail(id), 'availability'] as const,
    queryFn: async (): Promise<BidGearAvailabilityRow[]> => {
      const response = await apiClient.get<BidGearAvailabilityRow[]>(
        `${PROJECTS_API}/bids/${id}/availability/`
      );
      return response.data;
    },
    enabled: id > 0,
  });
}

export function useDownloadProofPackPdf() {
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      const response = await apiClient.get(`${PROJECTS_API}/projects/${id}/proof-pack/`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data as BlobPart]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        filenameFromContentDisposition(
          response.headers['content-disposition'],
          `ProofPack-${id}.pdf`,
        ),
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
  });
}

// ─── Invoice Hooks ──────────────────────────────────────────────────────────

export function useInvoices(filters: InvoiceFilters = {}) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.invoices.list(clean),
    queryFn: async (): Promise<PaginatedResponse<Invoice>> => {
      const response = await apiClient.get<PaginatedResponse<Invoice>>(
        `${PROJECTS_API}/invoices/`,
        { params: clean }
      );
      return response.data;
    },
  });
}

export function useInvoice(id: number) {
  return useQuery({
    queryKey: queryKeys.invoices.detail(id),
    queryFn: async (): Promise<Invoice> => {
      const response = await apiClient.get<Invoice>(`${PROJECTS_API}/invoices/${id}/`);
      return response.data;
    },
    enabled: id > 0,
  });
}

export function useCreateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateInvoicePayload): Promise<Invoice> => {
      const response = await apiClient.post<Invoice>(`${PROJECTS_API}/invoices/`, data);
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.invoices.all });
    },
  });
}

export function useUpdateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateInvoicePayload }): Promise<Invoice> => {
      const response = await apiClient.patch<Invoice>(`${PROJECTS_API}/invoices/${id}/`, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.invoices.all });
      qc.invalidateQueries({ queryKey: queryKeys.invoices.detail(variables.id) });
    },
  });
}

export function useSendInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<Invoice> => {
      const response = await apiClient.post<Invoice>(`${PROJECTS_API}/invoices/${id}/send/`);
      return response.data;
    },
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: queryKeys.invoices.all });
      qc.invalidateQueries({ queryKey: queryKeys.invoices.detail(id) });
    },
  });
}

export interface PullInvoiceExpensesResult {
  invoice_lines_created?: number;
  invoice?: Invoice;
  detail?: string;
}

export function usePullInvoiceExpenses() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<PullInvoiceExpensesResult> => {
      const response = await apiClient.post<PullInvoiceExpensesResult>(
        `${PROJECTS_API}/invoices/${id}/pull-expenses/`
      );
      return response.data;
    },
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: queryKeys.invoices.all });
      qc.invalidateQueries({ queryKey: queryKeys.invoices.detail(id) });
    },
  });
}

export function useVoidInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<Invoice> => {
      const response = await apiClient.post<Invoice>(`${PROJECTS_API}/invoices/${id}/void/`);
      return response.data;
    },
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: queryKeys.invoices.all });
      qc.invalidateQueries({ queryKey: queryKeys.invoices.detail(id) });
    },
  });
}

export function useMarkInvoicePaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<Invoice> => {
      const response = await apiClient.post<Invoice>(`${PROJECTS_API}/invoices/${id}/mark-paid/`);
      return response.data;
    },
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: queryKeys.invoices.all });
      qc.invalidateQueries({ queryKey: queryKeys.invoices.detail(id) });
    },
  });
}

export function useDuplicateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<Invoice> => {
      const response = await apiClient.post<Invoice>(`${PROJECTS_API}/invoices/${id}/duplicate/`);
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.invoices.all });
    },
  });
}

export function useDownloadInvoicePdf() {
  return useMutation({
    mutationFn: async ({ id, includeEvidence }: { id: number; includeEvidence?: boolean }): Promise<void> => {
      const requestUrl = includeEvidence
        ? `${PROJECTS_API}/invoices/${id}/pdf/?include_evidence=true`
        : `${PROJECTS_API}/invoices/${id}/pdf/`;
      const response = await apiClient.get(requestUrl, {
        responseType: 'blob',
      });
      const objectUrl = window.URL.createObjectURL(new Blob([response.data as BlobPart]));
      const link = document.createElement('a');
      link.href = objectUrl;
      link.setAttribute(
        'download',
        filenameFromContentDisposition(
          response.headers['content-disposition'],
          `Invoice-${id}.pdf`,
        ),
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    },
  });
}

export function useGenerateInvoiceFromTime() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { project_id: number; date_from: string; date_to: string }): Promise<Invoice> => {
      const response = await apiClient.post<Invoice>(
        `${PROJECTS_API}/invoices/generate-from-time/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.invoices.all });
    },
  });
}

export function useReleaseRetainage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { project_id: number }): Promise<Invoice> => {
      const response = await apiClient.post<Invoice>(
        `${PROJECTS_API}/invoices/release-retainage/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.invoices.all });
      qc.invalidateQueries({ queryKey: queryKeys.projects.all });
    },
  });
}

// ─── Invoice Payment Hooks ──────────────────────────────────────────────────

export function useRecordPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateInvoicePaymentPayload): Promise<InvoicePayment> => {
      const response = await apiClient.post<InvoicePayment>(
        `${PROJECTS_API}/invoices/${data.invoice}/record-payment/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.invoices.all });
      qc.invalidateQueries({ queryKey: queryKeys.invoices.detail(variables.invoice) });
      qc.invalidateQueries({ queryKey: queryKeys.invoicePayments.all });
    },
  });
}

// ─── Time Entry Hooks ───────────────────────────────────────────────────────

export function useTimeEntries(filters: TimeEntryFilters = {}) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.timeEntries.list(clean),
    queryFn: async (): Promise<PaginatedResponse<TimeEntry>> => {
      const response = await apiClient.get<PaginatedResponse<TimeEntry>>(
        `${PROJECTS_API}/time-entries/`,
        { params: clean }
      );
      return response.data;
    },
  });
}

export function useCreateTimeEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateTimeEntryPayload): Promise<TimeEntry> => {
      const response = await apiClient.post<TimeEntry>(`${PROJECTS_API}/time-entries/`, data);
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.timeEntries.all });
      qc.invalidateQueries({ queryKey: queryKeys.projects.all });
    },
  });
}

export function useUpdateTimeEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateTimeEntryPayload }): Promise<TimeEntry> => {
      const response = await apiClient.patch<TimeEntry>(`${PROJECTS_API}/time-entries/${id}/`, data);
      return response.data;
    },
    onSuccess: (result, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.timeEntries.all });
      qc.invalidateQueries({ queryKey: queryKeys.timeEntries.detail(variables.id) });
      qc.invalidateQueries({ queryKey: queryKeys.projects.detail(result.project) });
    },
  });
}

export function useDeleteTimeEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.delete(`${PROJECTS_API}/time-entries/${id}/`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.timeEntries.all });
      qc.invalidateQueries({ queryKey: queryKeys.projects.all });
    },
  });
}

export function useApproveTimeEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      variables: number | { id: number; confirmSelfApproval?: boolean },
    ): Promise<TimeEntry> => {
      const { id, confirmSelfApproval } =
        typeof variables === 'number' ? { id: variables, confirmSelfApproval: false } : variables;
      const response = await apiClient.post<TimeEntry>(
        `${PROJECTS_API}/time-entries/${id}/approve/`,
        confirmSelfApproval ? { confirm_self_approval: true } : undefined
      );
      return response.data;
    },
    onSuccess: (result) => {
      const id = result.id;
      qc.invalidateQueries({ queryKey: queryKeys.timeEntries.all });
      qc.invalidateQueries({ queryKey: queryKeys.timeEntries.detail(id) });
      qc.invalidateQueries({ queryKey: queryKeys.projects.detail(result.project) });
    },
  });
}

export function useBulkCreateTimeEntries() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: BulkTimeEntryPayload): Promise<TimeEntry[]> => {
      const response = await apiClient.post<TimeEntry[]>(
        `${PROJECTS_API}/time-entries/bulk-create/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.timeEntries.all });
      qc.invalidateQueries({ queryKey: queryKeys.projects.all });
    },
  });
}

// ─── Project-Scoped Time Entry Hooks ─────────────────────────────────────────

export function useProjectTimeEntries(
  projectId: number,
  filters: Omit<TimeEntryFilters, 'project'> = {},
  enabled = true,
) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: [...queryKeys.projects.detail(projectId), 'time-entries', clean] as const,
    queryFn: async (): Promise<PaginatedResponse<TimeEntry>> => {
      const response = await apiClient.get<PaginatedResponse<TimeEntry>>(
        `${PROJECTS_API}/projects/${projectId}/time-entries/`,
        { params: clean },
      );
      return response.data;
    },
    enabled: projectId > 0 && enabled,
  });
}

export function useCreateProjectTimeEntry(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      data: Omit<CreateTimeEntryPayload, 'project'>,
    ): Promise<TimeEntry> => {
      const response = await apiClient.post<TimeEntry>(
        `${PROJECTS_API}/projects/${projectId}/time-entries/`,
        data,
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.projects.detail(projectId) });
      qc.invalidateQueries({ queryKey: queryKeys.timeEntries.all });
    },
  });
}

// ─── Time Summary Hook ───────────────────────────────────────────────────────

export interface TimeSummaryFilters {
  date_from?: string;
  date_to?: string;
}

export interface TimeSummaryRow {
  cost_code_id: number | null;
  code: string | null;
  name: string;
  total_hours: string;
  total_billable_hours: string;
  /** Omitted by the API for users without H3Books billing visibility. */
  total_cost?: string;
}

export function useProjectTimeSummary(
  projectId: number,
  filters: TimeSummaryFilters = {},
  enabled = true,
) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: [...queryKeys.projects.detail(projectId), 'time-summary', clean] as const,
    queryFn: async (): Promise<TimeSummaryRow[]> => {
      const response = await apiClient.get<TimeSummaryRow[]>(
        `${PROJECTS_API}/projects/${projectId}/time-summary/`,
        { params: clean },
      );
      return response.data;
    },
    enabled: projectId > 0 && enabled,
  });
}

export interface TimeEntrySummary {
  total_hours: string;
}

export function useTimeEntriesSummary(filters: TimeSummaryFilters = {}) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.timeEntries.summary(clean),
    queryFn: async (): Promise<TimeEntrySummary> => {
      const response = await apiClient.get<TimeEntrySummary>(
        `${PROJECTS_API}/time-entries/summary/`,
        { params: clean },
      );
      return response.data;
    },
  });
}

// ─── Client Portal Access Hooks ──────────────────────────────────────────────

export interface PortalAccessFilters {
  client?: number;
}

export function usePortalAccesses(filters: PortalAccessFilters = {}, enabled = true) {
  const cleaned = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.portalAccess.list(cleaned),
    queryFn: async (): Promise<PaginatedResponse<ClientPortalAccess>> => {
      const response = await apiClient.get<PaginatedResponse<ClientPortalAccess>>(
        `${PROJECTS_API}/portal-access/`,
        { params: cleaned }
      );
      return response.data;
    },
    enabled,
  });
}

export function useCreatePortalAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateClientPortalAccessPayload): Promise<ClientPortalAccess> => {
      const response = await apiClient.post<ClientPortalAccess>(
        `${PROJECTS_API}/portal-access/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.portalAccess.all });
    },
  });
}

export function useUpdatePortalAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateClientPortalAccessPayload }): Promise<ClientPortalAccess> => {
      const response = await apiClient.patch<ClientPortalAccess>(
        `${PROJECTS_API}/portal-access/${id}/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: queryKeys.portalAccess.all });
      qc.invalidateQueries({ queryKey: queryKeys.portalAccess.detail(id) });
    },
  });
}

export function useDeletePortalAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.delete(`${PROJECTS_API}/portal-access/${id}/`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.portalAccess.all });
    },
  });
}

export function useRegeneratePortalToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<ClientPortalAccess> => {
      const response = await apiClient.post<ClientPortalAccess>(
        `${PROJECTS_API}/portal-access/${id}/regenerate-token/`
      );
      return response.data;
    },
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: queryKeys.portalAccess.all });
      qc.invalidateQueries({ queryKey: queryKeys.portalAccess.detail(id) });
    },
  });
}

// ─── Recurring Invoice Config Hooks ──────────────────────────────────────────

export interface RecurringInvoiceConfigFilters {
  project?: number;
}

export function useRecurringInvoiceConfigs(filters: RecurringInvoiceConfigFilters = {}) {
  const cleaned = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.recurringInvoiceConfigs.list(cleaned),
    queryFn: async (): Promise<RecurringInvoiceConfig[]> => {
      const response = await apiClient.get<RecurringInvoiceConfig[]>(
        `${PROJECTS_API}/recurring-invoice-configs/`,
        { params: cleaned }
      );
      return response.data;
    },
  });
}

export function useCreateRecurringInvoiceConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateRecurringInvoiceConfigPayload): Promise<RecurringInvoiceConfig> => {
      const response = await apiClient.post<RecurringInvoiceConfig>(
        `${PROJECTS_API}/recurring-invoice-configs/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.recurringInvoiceConfigs.all });
    },
  });
}

export function useUpdateRecurringInvoiceConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateRecurringInvoiceConfigPayload }): Promise<RecurringInvoiceConfig> => {
      const response = await apiClient.patch<RecurringInvoiceConfig>(
        `${PROJECTS_API}/recurring-invoice-configs/${id}/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: queryKeys.recurringInvoiceConfigs.all });
      qc.invalidateQueries({ queryKey: queryKeys.recurringInvoiceConfigs.detail(id) });
    },
  });
}

export function useDeleteRecurringInvoiceConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.delete(`${PROJECTS_API}/recurring-invoice-configs/${id}/`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.recurringInvoiceConfigs.all });
    },
  });
}

// ─── Change Order Hooks ──────────────────────────────────────────────────────

export interface ChangeOrderFilters {
  project?: number;
  status?: string;
}

export function useChangeOrders(filters: ChangeOrderFilters = {}) {
  const cleaned = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.changeOrders.list(cleaned),
    queryFn: async (): Promise<ChangeOrder[]> => {
      const response = await apiClient.get<ChangeOrder[]>(
        `${PROJECTS_API}/change-orders/`,
        { params: cleaned }
      );
      return response.data;
    },
  });
}

export function useCreateChangeOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateChangeOrderPayload): Promise<ChangeOrder> => {
      const response = await apiClient.post<ChangeOrder>(
        `${PROJECTS_API}/change-orders/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.changeOrders.all });
      qc.invalidateQueries({ queryKey: queryKeys.projects.all });
    },
  });
}

export function useUpdateChangeOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateChangeOrderPayload }): Promise<ChangeOrder> => {
      const response = await apiClient.patch<ChangeOrder>(
        `${PROJECTS_API}/change-orders/${id}/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: queryKeys.changeOrders.all });
      qc.invalidateQueries({ queryKey: queryKeys.changeOrders.detail(id) });
    },
  });
}

export function useApproveChangeOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<ChangeOrder> => {
      const response = await apiClient.post<ChangeOrder>(
        `${PROJECTS_API}/change-orders/${id}/approve/`
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.changeOrders.all });
      qc.invalidateQueries({ queryKey: queryKeys.projects.all });
    },
  });
}

export function useRejectChangeOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<ChangeOrder> => {
      const response = await apiClient.post<ChangeOrder>(
        `${PROJECTS_API}/change-orders/${id}/reject/`
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.changeOrders.all });
      qc.invalidateQueries({ queryKey: queryKeys.projects.all });
    },
  });
}

// ─── Change Order Line Items ─────────────────────────────────────────────────

export function useCreateChangeOrderLineItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateChangeOrderLineItemPayload): Promise<ChangeOrderLineItem> => {
      const response = await apiClient.post<ChangeOrderLineItem>(
        `${PROJECTS_API}/change-order-line-items/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.changeOrderLineItems.all });
      qc.invalidateQueries({ queryKey: queryKeys.changeOrders.all });
    },
  });
}

export function useUpdateChangeOrderLineItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateChangeOrderLineItemPayload }): Promise<ChangeOrderLineItem> => {
      const response = await apiClient.patch<ChangeOrderLineItem>(
        `${PROJECTS_API}/change-order-line-items/${id}/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.changeOrderLineItems.all });
      qc.invalidateQueries({ queryKey: queryKeys.changeOrders.all });
    },
  });
}

export function useDeleteChangeOrderLineItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.delete(`${PROJECTS_API}/change-order-line-items/${id}/`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.changeOrderLineItems.all });
      qc.invalidateQueries({ queryKey: queryKeys.changeOrders.all });
    },
  });
}

// ─── Portal (Public) Hooks ───────────────────────────────────────────────────

const PORTAL_API = '/api/v1/projects/portal';

export function usePortalValidate(token: string) {
  return useQuery({
    queryKey: queryKeys.portal.validate(token),
    queryFn: async (): Promise<PortalSessionInfo> => {
      const response = await apiClient.get<PortalSessionInfo>(
        `${PORTAL_API}/validate/`
      );
      return response.data;
    },
    enabled: !!token,
    staleTime: 5 * 60 * 1000, // cache for 5 min to avoid excessive validation calls
  });
}

export function usePortalProjects(token: string) {
  return useQuery({
    queryKey: queryKeys.portal.projects(token),
    queryFn: async (): Promise<PortalProject[]> => {
      const response = await apiClient.get<PortalProject[]>(
        `${PORTAL_API}/projects/`
      );
      return response.data;
    },
    enabled: !!token,
  });
}

export function usePortalBids(token: string) {
  return useQuery({
    queryKey: queryKeys.portal.bids(token),
    queryFn: async (): Promise<PortalBid[]> => {
      const response = await apiClient.get<PortalBid[]>(
        `${PORTAL_API}/bids/`
      );
      return response.data;
    },
    enabled: !!token,
  });
}

export function usePortalBid(token: string, id: number) {
  return useQuery({
    queryKey: queryKeys.portal.bid(token, id),
    queryFn: async (): Promise<PortalBid> => {
      const response = await apiClient.get<PortalBid>(
        `${PORTAL_API}/bids/${id}/`
      );
      return response.data;
    },
    enabled: !!token && !!id,
  });
}

export function usePortalApproveBid(token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      bidId,
      signature_png_base64,
      signer_name,
    }: {
      bidId: number;
      signature_png_base64: string;
      signer_name: string;
    }): Promise<{ detail: string; status: string }> => {
      const response = await apiClient.post(
        `${PORTAL_API}/bids/${bidId}/approve/`,
        { signature_png_base64, signer_name }
      );
      return response.data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.portal.bids(token) });
      qc.invalidateQueries({ queryKey: queryKeys.portal.bid(token, variables.bidId) });
    },
  });
}

export function usePortalRejectBid(token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ bidId, reason }: { bidId: number; reason?: string }): Promise<{ detail: string; status: string }> => {
      const response = await apiClient.post(
        `${PORTAL_API}/bids/${bidId}/reject/`,
        { reason }
      );
      return response.data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.portal.bids(token) });
      qc.invalidateQueries({ queryKey: queryKeys.portal.bid(token, variables.bidId) });
    },
  });
}

export function usePortalInvoices(token: string) {
  return useQuery({
    queryKey: queryKeys.portal.invoices(token),
    queryFn: async (): Promise<PortalInvoice[]> => {
      const response = await apiClient.get<PortalInvoice[]>(
        `${PORTAL_API}/invoices/`
      );
      return response.data;
    },
    enabled: !!token,
  });
}

export function usePortalPayInvoice(token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (invoiceId: number): Promise<{ checkout_url: string }> => {
      const response = await apiClient.post<{ checkout_url: string }>(
        `${PORTAL_API}/invoices/${invoiceId}/pay/`
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.portal.invoices(token) });
    },
  });
}

export function usePortalChangeOrders(token: string) {
  return useQuery({
    queryKey: queryKeys.portal.changeOrders(token),
    queryFn: async (): Promise<PortalChangeOrder[]> => {
      const response = await apiClient.get<PortalChangeOrder[]>(
        `${PORTAL_API}/change-orders/`
      );
      return response.data;
    },
    enabled: !!token,
  });
}

export function usePortalApproveChangeOrder(token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      coId,
      signature_png_base64,
      signer_name,
    }: {
      coId: number;
      signature_png_base64: string;
      signer_name: string;
    }): Promise<{ detail: string; status: string }> => {
      const response = await apiClient.post(
        `${PORTAL_API}/change-orders/${coId}/approve/`,
        { signature_png_base64, signer_name }
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.portal.changeOrders(token) });
    },
  });
}


// ─── Portal Project Activity ──────────────────────────────────────────────

export function usePortalProjectActivity(token: string, projectId: number, page: number) {
  return useQuery({
    queryKey: queryKeys.portal.activity(token, projectId, page),
    queryFn: async (): Promise<ProjectActivityPage> => {
      const response = await apiClient.get<ProjectActivityPage>(
        `${PORTAL_API}/projects/${projectId}/activity/`,
        { params: { page } }
      );
      return response.data;
    },
    enabled: !!token && !!projectId,
  });
}

// ─── Internal Project Activity ────────────────────────────────────────────

export function useProjectActivity(projectId: number, page: number) {
  return useQuery({
    queryKey: queryKeys.projects.activity(projectId, page),
    queryFn: async (): Promise<ProjectActivityPage> => {
      const response = await apiClient.get<ProjectActivityPage>(
        `${PROJECTS_API}/projects/${projectId}/activity/`,
        { params: { page } }
      );
      return response.data;
    },
    enabled: !!projectId,
  });
}

export function usePortalRejectChangeOrder(token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ coId, reason }: { coId: number; reason?: string }): Promise<{ detail: string; status: string }> => {
      const response = await apiClient.post(
        `${PORTAL_API}/change-orders/${coId}/reject/`,
        { reason }
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.portal.changeOrders(token) });
    },
  });
}

export function usePortalSafety(token: string, projectId: number) {
  return useQuery({
    queryKey: queryKeys.portal.safety(token, projectId),
    queryFn: async (): Promise<PortalSafetyResponse> => {
      const response = await apiClient.get<PortalSafetyResponse>(
        `${PORTAL_API}/projects/${projectId}/safety/`
      );
      return response.data;
    },
    enabled: !!token && !!projectId,
  });
}

// ─── Project Cost Codes ─────────────────────────────────────────────────────

export function useProjectCostCodes(projectId: number, enabled = true) {
  return useQuery({
    queryKey: queryKeys.projects.costCodes(projectId),
    queryFn: async (): Promise<CostCode[]> => {
      const response = await apiClient.get<CostCode[]>(
        `${PROJECTS_API}/projects/${projectId}/cost-codes/`
      );
      return response.data;
    },
    enabled: projectId > 0 && enabled,
  });
}
